import { Settlement } from '../../../models/Entities/Settlements/Settlement';
import { SettlementStatusEnum } from '../../../models/Enums/SettlementStatusEnum';
import { MonthSelection, monthKey } from '../MonthSelectorBar/MonthSelectorBar.Functions';

/** Lógica de la lista de liquidaciones, sin React ni store. */

export type StatusTone = 'neutral' | 'progress' | 'finished' | 'confirmed' | 'paid' | 'danger' | 'critical';

const STATUS_META: Record<number, { tone: StatusTone; key: string }> = {
    [SettlementStatusEnum.REGISTERED]: { tone: 'neutral', key: 'SettlementStatus.registered' },
    [SettlementStatusEnum.IN_PROGRESS]: { tone: 'progress', key: 'SettlementStatus.inProcess' },
    [SettlementStatusEnum.FINISHED]: { tone: 'finished', key: 'SettlementStatus.finished' },
    [SettlementStatusEnum.CANCELLED]: { tone: 'neutral', key: 'SettlementStatus.cancelled' },
    [SettlementStatusEnum.CONFIRMED]: { tone: 'confirmed', key: 'SettlementStatus.Confirmed' },
    [SettlementStatusEnum.REJECTED]: { tone: 'danger', key: 'SettlementStatus.rejected' },
    [SettlementStatusEnum.FAIL]: { tone: 'danger', key: 'SettlementStatus.fail' },
    [SettlementStatusEnum.PAID]: { tone: 'paid', key: 'SettlementStatus.paid' },
    [SettlementStatusEnum.DEBT_UNPAID]: { tone: 'critical', key: 'SettlementStatus.debtUnpaid' }
};

export const statusMeta = (status: number | undefined): { tone: StatusTone; key: string } =>
    (status !== undefined && STATUS_META[status]) || { tone: 'neutral', key: 'SettlementStatus.pending' };

/** Orden en que se muestran las pastillas de estado: como avanza una liquidación. */
export const STATUS_ORDER: number[] = [
    SettlementStatusEnum.REGISTERED,
    SettlementStatusEnum.IN_PROGRESS,
    SettlementStatusEnum.FINISHED,
    SettlementStatusEnum.CONFIRMED,
    SettlementStatusEnum.PAID,
    SettlementStatusEnum.DEBT_UNPAID,
    SettlementStatusEnum.REJECTED,
    SettlementStatusEnum.FAIL,
    SettlementStatusEnum.CANCELLED
];

/** Cuántas liquidaciones hay por estado, en el orden de STATUS_ORDER y sólo los que aparecen. */
export const statusCounts = (settlements: Settlement[]): { status: number; count: number }[] => {
    const counts = new Map<number, number>();
    settlements.forEach((item) => counts.set(item.status, (counts.get(item.status) ?? 0) + 1));
    return STATUS_ORDER.filter((status) => counts.has(status)).map((status) => ({ status, count: counts.get(status)! }));
};

/** El mes de una liquidación: el inicio de su período. */
export const settlementMonthKey = (settlement: Settlement): string | undefined => {
    if (!settlement.settlementDateRangeInit) return undefined;
    const date = new Date(settlement.settlementDateRangeInit);
    return Number.isNaN(date.getTime()) ? undefined : monthKey(date.getFullYear(), date.getMonth() + 1);
};

export const inSelectedMonths = (settlement: Settlement, months: MonthSelection[]): boolean => {
    const key = settlementMonthKey(settlement);
    return !!key && months.some((month) => monthKey(month.year, month.month) === key);
};

const normalize = (text: string | undefined | null): string =>
    (text || '').toLowerCase().normalize('NFD').replace(new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g'), '');

export interface SearchableSettlement {
    settlement: Settlement;
    /** Nombre del protocolo, sponsor, médico u obra social ya resueltos. */
    protocolLabel?: string;
    entityLabel?: string;
}

/** Busca en nombre, identificador, factura, protocolo y contraparte. */
export const matchesSearch = (item: SearchableSettlement, query: string): boolean => {
    const needle = normalize(query).trim();
    if (!needle) return true;
    const haystack = [
        item.settlement.name,
        item.settlement.identifier,
        item.settlement.voucheredNumber,
        item.settlement.bussinessname,
        item.protocolLabel,
        item.entityLabel
    ].map(normalize).join(' ');
    return needle.split(/\s+/).every((word) => haystack.includes(word));
};

export interface ProtocolGroup {
    key: string;
    label: string;
    items: Settlement[];
}

/** Agrupa por protocolo (o por contraparte si no hay protocolo), en el orden de aparición. */
export const groupByProtocol = (items: SearchableSettlement[]): ProtocolGroup[] => {
    const groups = new Map<string, ProtocolGroup>();
    items.forEach((item) => {
        const label = item.protocolLabel || item.entityLabel || 'Sin protocolo';
        const group = groups.get(label) ?? { key: label, label, items: [] };
        group.items.push(item.settlement);
        groups.set(label, group);
    });
    return Array.from(groups.values());
};

/** Etiqueta del mes elegido: "Septiembre 2026", o cuántos meses hay si son varios. */
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export const selectionLabel = (months: MonthSelection[]): string => {
    if (months.length === 1) return `${MONTHS[months[0].month - 1]} ${months[0].year}`;
    const sorted = [...months].sort((a, b) => a.year * 12 + a.month - (b.year * 12 + b.month));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    return `${MONTHS[first.month - 1].slice(0, 3)} ${first.year} – ${MONTHS[last.month - 1].slice(0, 3)} ${last.year} · ${months.length} meses`;
};
